import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import ScheduleEntryForm from './ScheduleEntryForm';
import { getCabDefinitionById } from '../../../services/cabDefinitionsService';
import { saveScheduleEntry } from '../../../services/cabScheduleService';
import {
  buildScheduleEntryPayload,
  createDefaultScheduleEntryFormData,
  hasScheduleValidationErrors,
  validateScheduleEntryForm,
  type ScheduleEntryFormErrors,
} from '../../../utils/cabScheduleValidation';
import type { ScheduleEntryFormData } from '../../../types/cabSchedule';

const ScheduleEntryCreate: React.FC = () => {
  const { id: cabDefinitionId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<ScheduleEntryFormData>(createDefaultScheduleEntryFormData);
  const [errors, setErrors] = useState<ScheduleEntryFormErrors>({});

  const { data: cabDef, isLoading } = useQuery({
    queryKey: ['cab-definition', cabDefinitionId],
    queryFn: () => getCabDefinitionById(cabDefinitionId!),
    enabled: Boolean(cabDefinitionId),
  });

  const saveMutation = useMutation({
    mutationFn: saveScheduleEntry,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cab-schedule-entries', cabDefinitionId] });
      queryClient.invalidateQueries({ queryKey: ['cab-meetings', cabDefinitionId] });
    },
  });

  const handleChange = (updates: Partial<ScheduleEntryFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    const keys = Object.keys(updates) as Array<keyof ScheduleEntryFormData>;
    setErrors((prev) => {
      const next = { ...prev };
      keys.forEach((k) => delete next[k]);
      if (keys.some((k) => ['fromDate', 'fromTime', 'toDate', 'toTime', 'allDay', 'repeats', 'repeatsUntil', 'repeatInterval', 'repeatWeekdays'].includes(k))) {
        delete next.dateTimeRange;
        if (keys.includes('repeats') && updates.repeats === '') {
          delete next.repeatsUntil;
          delete next.repeatInterval;
          delete next.repeatWeekdays;
        }
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!cabDefinitionId) return;

    const validationErrors = validateScheduleEntryForm(formData);
    if (hasScheduleValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      toast.error('Please fix the validation errors before submitting.');
      return;
    }

    try {
      const payload = buildScheduleEntryPayload(cabDefinitionId, formData);
      const result = await saveMutation.mutateAsync(payload);
      toast.success(
        `Schedule entry saved. ${result.meetings.length} CAB meeting(s) generated.`
      );
      navigate(`/admin/cab-meeting-management/cab-definitions/${cabDefinitionId}?tab=schedule`);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to save schedule entry.');
    }
  };

  const handleCancel = () => {
    navigate(`/admin/cab-meeting-management/cab-definitions/${cabDefinitionId}?tab=schedule`);
  };

  if (isLoading) {
    return <div className="p-6 text-gray-600">Loading...</div>;
  }

  if (!cabDef) {
    return <div className="p-6 text-gray-600">CAB Definition not found.</div>;
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-auto">
      <nav className="mb-2 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li><ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden /></li>
          <li>
            <Link to="/admin/cab-meeting-management/cab-definitions" className="hover:text-indigo-600">
              CAB Meeting Management
            </Link>
          </li>
          <li><ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden /></li>
          <li>
            <Link
              to={`/admin/cab-meeting-management/cab-definitions/${cabDefinitionId}?tab=schedule`}
              className="hover:text-indigo-600"
            >
              {cabDef.name}
            </Link>
          </li>
          <li><ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden /></li>
          <li className="text-gray-900 font-medium">New Schedule Entry</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">Schedule Entry</h1>
          <p className="text-sm text-gray-600 mt-1">New record</p>
        </div>

        <ScheduleEntryForm
          formData={formData}
          errors={errors}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSaving={saveMutation.isPending}
        />
      </div>
    </div>
  );
};

export default ScheduleEntryCreate;
